import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Container,
  Breadcrumbs,
  Link as MUILink,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { message, Modal } from "antd";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import axios from "../../axiosInstance";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import Table from "../common/Table";

const API_URL = "/api/v1/school-fees";

const installmentOptions = [
  "1 Installment",
  "2 Installments",
  "3 Installments",
  "4 Installments",
  "Complete Payment",
];

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const SchoolFee = () => {
  const [value, setValue] = useState(0);
  const [fees, setFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentName: "",
    parentName: "",
    rollNumber: "",
    className: "",
    totalAmount: "",
    installmentType: "",
    amount: "",
    paymentDate: "",
  });

  const [editDialog, setEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Fetch fees
  const fetchFees = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/all`);
      setFees(res.data.data);
      setFilteredFees(res.data.data);
    } catch (err) {
      console.error(err);
      message.error("Error fetching fees data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFees();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearch(searchValue);

    if (searchValue === "") {
      setFilteredFees(fees);
    } else {
      const filtered = fees.filter(
        (fee) =>
          fee.studentName?.toLowerCase().includes(searchValue) ||
          fee.parentName?.toLowerCase().includes(searchValue) ||
          fee.rollNumber?.toLowerCase().includes(searchValue) ||
          fee.className?.toLowerCase().includes(searchValue)
      );
      setFilteredFees(filtered);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleInstallmentTypeChange = (type, record = null) => {
    let installmentAmount = 0;
    const total = parseFloat(formData.totalAmount) || 0;

    switch (type) {
      case "1 Installment":
        installmentAmount = total / 4;
        break;
      case "2 Installments":
        installmentAmount = total / 2;
        break;
      case "3 Installments":
        installmentAmount = total / 3;
        break;
      case "4 Installments":
        installmentAmount = total / 4;
        break;
      case "Complete Payment":
        installmentAmount = total;
        break;
      default:
        installmentAmount = 0;
    }

    if (record) {
      const existingInst = record.installments.find(
        (inst) => inst.installmentType === type
      );
      if (existingInst)
        installmentAmount = existingInst.amount || installmentAmount;
    }

    setFormData({
      ...formData,
      installmentType: type,
      amount: installmentAmount,
    });
  };

  const handleAddFee = async () => {
    if (
      !formData.studentName ||
      !formData.totalAmount ||
      !formData.installmentType ||
      !formData.amount
    ) {
      message.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/add`, {
        studentName: formData.studentName,
        parentName: formData.parentName,
        rollNumber: formData.rollNumber,
        className: formData.className,
        totalAmount: formData.totalAmount,
        installments: [
          {
            installmentType: formData.installmentType,
            amount: formData.amount,
            paymentDate: formData.paymentDate,
          },
        ],
      });
      message.success("Fee added successfully");
      setFormData({
        studentName: "",
        parentName: "",
        rollNumber: "",
        className: "",
        totalAmount: "",
        installmentType: "",
        amount: "",
        paymentDate: "",
      });
      fetchFees();
      setValue(0);
    } catch (err) {
      console.error(err);
      message.error("Error adding fee");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Dialog
  const handleEditClick = (record) => {
    setSelectedRecord(record);
    setFormData({
      installmentType: record.installments[0]?.installmentType || "",
      amount: record.installments[0]?.amount || "",
      paymentDate: record.installments[0]?.paymentDate
        ? record.installments[0]?.paymentDate.split("T")[0]
        : "",
    });
    setEditDialog(true);
  };

  // Update Installment
  const handleUpdateInstallment = async () => {
    if (
      !formData.installmentType ||
      !formData.amount ||
      !formData.paymentDate
    ) {
      message.error("Please fill all fields");
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API_URL}/update/${selectedRecord._id}`, {
        installmentType: formData.installmentType,
        amount: formData.amount,
        paymentDate: formData.paymentDate,
      });
      message.success("Installment updated successfully");
      setEditDialog(false);
      fetchFees();
    } catch (err) {
      console.error(err);
      message.error("Error updating installment");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this fee record?",
      content: "Once deleted, this record cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/delete/${id}`);
          message.success("Record deleted successfully");
          fetchFees();
        } catch (err) {
          console.error(err);
          message.error("Error deleting record");
        }
      },
    });
  };

  // PDF Export Function - FIXED VERSION
  const AllStudentsGeneratePDF = () => {
    if (filteredFees.length === 0) {
      message.warning("No fee records to export");
      return;
    }

    Modal.confirm({
      title: "Confirm Download",
      content: "Would you like to download the fee records as a PDF file?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();

        // Title
        doc.setFontSize(18);
        doc.setTextColor(0, 51, 102);
        doc.text("School Fee Records Report", 105, 20, { align: "center" });

        // Summary Information
        let startY = 40;

        // Total Records
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`Total Records: ${filteredFees.length}`, 14, startY);

        // Total Amount Summary
        const totalAmount = filteredFees.reduce(
          (sum, fee) => sum + (parseFloat(fee.totalAmount) || 0),
          0
        );
        const totalPaid = filteredFees.reduce((sum, fee) => {
          const paid = fee.installments.reduce(
            (instSum, inst) => instSum + (parseFloat(inst.amount) || 0),
            0
          );
          return sum + paid;
        }, 0);
        const totalPending = totalAmount - totalPaid;

        doc.text(
          `Total Amount: ${totalAmount.toLocaleString()}`,
          14,
          startY + 6
        );
        doc.text(`Total Paid: ${totalPaid.toLocaleString()}`, 14, startY + 12);
        doc.text(
          `Total Pending: ${totalPending.toLocaleString()}`,
          14,
          startY + 18
        );

        startY += 30;

        // Table columns - SIMPLIFIED for better PDF display
        const columns = [
          { header: "#", dataKey: "index" },
          { header: "Student", dataKey: "studentName" },
          { header: "Class", dataKey: "className" },
          { header: "Roll No", dataKey: "rollNumber" },
          { header: "Total Amount", dataKey: "totalAmount" },
          { header: "Paid", dataKey: "paidAmount" },
          { header: "Pending", dataKey: "pendingPayment" },
          { header: "Date", dataKey: "paymentDate" },
        ];

        // Table data - SIMPLIFIED for better PDF display
        const feeData = filteredFees.map((fee, index) => {
          const paidAmount = fee.installments.reduce(
            (sum, inst) => sum + (parseFloat(inst.amount) || 0),
            0
          );
          const pendingPayment = fee.totalAmount - paidAmount;
          // const status = pendingPayment === 0 ? "Paid" : pendingPayment === fee.totalAmount ? "Unpaid" : "Partial";

          return {
            index: index + 1,
            studentName: fee.studentName || "N/A",
            className: fee.className || "N/A",
            rollNumber: fee.rollNumber || "N/A",
            totalAmount: `${fee.totalAmount}`,
            paidAmount: `${paidAmount}`,
            pendingPayment: `${pendingPayment}`,
            paymentDate: fee.installments[0]?.paymentDate
              ? moment(fee.installments[0]?.paymentDate).format("DD/MM/YYYY")
              : "N/A",
          };
        });

        // Add table to the PDF
        autoTable(doc, {
          columns: columns,
          body: feeData,
          startY: startY,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [0, 51, 102],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          columnStyles: {
            0: { cellWidth: 12, halign: "center" }, // #
            1: { cellWidth: 35 }, // Student
            2: { cellWidth: 25 }, // Class
            3: { cellWidth: 20, halign: "center" }, // Roll No
            4: { cellWidth: 25, halign: "right" }, // Total
            5: { cellWidth: 25, halign: "right" }, // Paid
            6: { cellWidth: 25, halign: "right" }, // Pending
            7: { cellWidth: 20, halign: "center" }, // Status
          },
          margin: { left: 10, right: 10 },
        });

        // Add page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Page ${i} of ${pageCount}`,
            doc.internal.pageSize.width - 20,
            doc.internal.pageSize.height - 10
          );
        }

        // Save the generated PDF
        doc.save(
          `School_Fee_Records_${moment().format("YYYY-MM-DD_HH-mm")}.pdf`
        );
        message.success("PDF exported successfully!");
      },
      onCancel() {
        console.log("Download canceled");
      },
    });
  };

  // Detailed PDF Export for individual records
  const exportDetailedPDF = (record) => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.setTextColor(0, 51, 102);
    doc.text("Student Fee Details", 105, 20, { align: "center" });

    // Student Information
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    let yPos = 40;

    doc.text(`Student Name: ${record.studentName || "N/A"}`, 20, yPos);
    doc.text(`Parent Name: ${record.parentName || "N/A"}`, 20, yPos + 8);
    doc.text(`Class: ${record.className || "N/A"}`, 20, yPos + 16);
    doc.text(`Roll No: ${record.rollNumber || "N/A"}`, 20, yPos + 24);
    doc.text(`Total Amount: ${record.totalAmount}`, 20, yPos + 32);

    yPos += 45;

    // Installment Details
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text("Installment Details:", 20, yPos);

    yPos += 10;

    record.installments.forEach((inst, index) => {
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `${index + 1}. ${inst.installmentType}: ${inst.amount}`,
        25,
        yPos
      );
      doc.text(
        `Date: ${
          inst.paymentDate
            ? moment(inst.paymentDate).format("DD/MM/YYYY")
            : "Not Paid"
        }`,
        120,
        yPos
      );
      yPos += 8;
    });

    // Summary
    const paidAmount = record.installments.reduce(
      (sum, inst) => sum + (parseFloat(inst.amount) || 0),
      0
    );
    const pendingPayment = record.totalAmount - paidAmount;

    yPos += 10;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Paid: ${paidAmount}`, 20, yPos);
    doc.text(`Pending Payment: ${pendingPayment}`, 20, yPos + 8);
    doc.text(
      `Status: ${
        pendingPayment === 0
          ? "Fully Paid"
          : pendingPayment === record.totalAmount
          ? "Unpaid"
          : "Partially Paid"
      }`,
      20,
      yPos + 16
    );

    doc.save(
      `Fee_Details_${record.studentName}_${moment().format("YYYY-MM-DD")}.pdf`
    );
  };

  const columns = [
    {
      field: "rowNumber",
      headerName: "#",
      width: 10,
      sortable: false,
    },
    {
      field: "studentName",
      headerName: "Student",
      width: 150,
    },
    {
      field: "parentName",
      headerName: "Parent",
      width: 150,
    },
    {
      field: "rollNumber",
      headerName: "Roll No",
      width: 120,
    },
    {
      field: "className",
      headerName: "Class",
      width: 120,
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      width: 130,
      renderCell: (params) => `₹${params.row.totalAmount}`,
    },
    {
      field: "installments",
      headerName: "Installments",
      width: 200,
      renderCell: (params) => (
        <Box>
          {params.row.installments.map((inst, index) => (
            <Typography key={index} variant="body2">
              {inst.installmentType}: ₹{inst.amount}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      field: "paymentDates",
      headerName: "Payment Dates",
      width: 200,
      renderCell: (params) => (
        <Box>
          {params.row.installments.map((inst, index) => (
            <Typography key={index} variant="body2">
              {inst.paymentDate
                ? new Date(inst.paymentDate).toLocaleDateString()
                : "Not Paid"}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      field: "pendingPayment",
      headerName: "Pending Payment",
      width: 150,
      renderCell: (params) => {
        const paidAmount = params.row.installments.reduce(
          (sum, inst) => sum + (parseFloat(inst.amount) || 0),
          0
        );
        const pendingPayment = params.row.totalAmount - paidAmount;
        return `₹${pendingPayment}`;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            aria-label="edit"
            onClick={() => handleEditClick(params.row)}
            sx={{ color: "#1976d2" }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => handleDelete(params.row._id)}
            sx={{ color: "red" }}
          >
            <DeleteIcon />
          </IconButton>
          {/* <IconButton
            aria-label="download"
            onClick={() => exportDetailedPDF(params.row)}
            sx={{ color: "green" }}
          >
            <DownloadIcon />
          </IconButton> */}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        School Fee Management
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>School Fees</Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="school fee tabs"
        >
          <Tab label="Fee Records" {...TabPanel(0)} />
          <Tab label="Add Fee Record" {...TabPanel(1)} />
        </Tabs>
      </Box>

      {/* Fee Records List */}
      <CustomTabPanel value={value} index={0}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Fee Records
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Student, Parent, Roll No or Class"
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#1976d2" }} />
                </IconButton>
              ),
            }}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={AllStudentsGeneratePDF}
          startIcon={<DownloadIcon />}
          disabled={filteredFees.length === 0}
          sx={{
            minWidth: "140px",
            borderRadius: "5px",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            letterSpacing: ".1rem",
          }}
        >
          Export to PDF
        </Button>
        <Box
          style={{
            width: "auto",
            overflowY: "hidden",
            overflowX: "scroll",
          }}
        >
          <Box
            sx={{
              pb: 2,
              display: "flex",
              width: "auto",
              justifyContent: "right",
            }}
          ></Box>
        </Box>
        <Table data={filteredFees} columns={columns} />
      </CustomTabPanel>

      {/* Add Fee Record */}
      <CustomTabPanel value={value} index={1}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Add New Fee Record
        </Typography>

        <Container
          component="form"
          className="form_style border-style"
          maxWidth={false}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="Student Name *"
              value={formData.studentName}
              onChange={(e) =>
                setFormData({ ...formData, studentName: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": {
                  color: "text.secondary",
                },
                fieldset: { borderColor: "rgb(231, 235, 240)" },
              }}
            />

            <TextField
              fullWidth
              label="Parent Name"
              value={formData.parentName}
              onChange={(e) =>
                setFormData({ ...formData, parentName: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": {
                  color: "text.secondary",
                },
                fieldset: { borderColor: "rgb(231, 235, 240)" },
              }}
            />

            <TextField
              fullWidth
              label="Roll Number"
              value={formData.rollNumber}
              onChange={(e) =>
                setFormData({ ...formData, rollNumber: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": {
                  color: "text.secondary",
                },
                fieldset: { borderColor: "rgb(231, 235, 240)" },
              }}
            />

            <TextField
              fullWidth
              label="Class Name"
              value={formData.className}
              onChange={(e) =>
                setFormData({ ...formData, className: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": {
                  color: "text.secondary",
                },
                fieldset: { borderColor: "rgb(231, 235, 240)" },
              }}
            />

            <TextField
              fullWidth
              type="number"
              label="Total Amount *"
              value={formData.totalAmount}
              onChange={(e) =>
                setFormData({ ...formData, totalAmount: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": {
                  color: "text.secondary",
                },
                "& input[type='number']": {
                  MozAppearance: "textfield",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                    {
                      display: "none",
                    },
                },
                fieldset: { borderColor: "rgb(231, 235, 240)" },
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="installment-type-label">
                Installment Type *
              </InputLabel>
              <Select
                labelId="installment-type-label"
                value={formData.installmentType}
                label="Installment Type *"
                onChange={(e) => handleInstallmentTypeChange(e.target.value)}
              >
                {installmentOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.installmentType && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  label="Paid Installment Amount *"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "text.secondary",
                    },
                    "& input[type='number']": {
                      MozAppearance: "textfield",
                      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                        {
                          display: "none",
                        },
                    },
                    fieldset: { borderColor: "rgb(231, 235, 240)" },
                  }}
                />

                <TextField
                  fullWidth
                  type="date"
                  label="Payment Date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.paymentDate}
                  onChange={(e) =>
                    setFormData({ ...formData, paymentDate: e.target.value })
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "text.secondary",
                    },
                    fieldset: { borderColor: "rgb(231, 235, 240)" },
                  }}
                />
              </>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddFee}
              disabled={loading}
              sx={{ mt: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : "Add Fee Record"}
            </Button>
          </Box>
        </Container>
      </CustomTabPanel>

      {/* Edit Installment Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins, sans-serif",
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Edit Installment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2, mt:2 }}>
            <InputLabel id="edit-installment-type-label">
              Installment Type
            </InputLabel>
            <Select
              labelId="edit-installment-type-label"
              value={formData.installmentType}
              label="Installment Type"
              onChange={(e) =>
                handleInstallmentTypeChange(e.target.value, selectedRecord)
              }
            >
              {installmentOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Paid Amount"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="date"
            label="Payment Date"
            InputLabelProps={{ shrink: true }}
            value={formData.paymentDate}
            onChange={(e) =>
              setFormData({ ...formData, paymentDate: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateInstallment}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolFee;
